import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { API_BASE_URL } from '../../environments/environment';
import { Router } from '@angular/router';
import { MobileZoomService } from '../common/mobile-zoom/mobile-zoom.service';
import { MatPaginator, MatDialog } from '@angular/material';
import { MapDialogComponent } from '../utility/map-dialog/map-dialog.component';
import { StaffDetailDialogComponent } from '../staffs/staff-detail-dialog/staff-detail-dialog.component';
import { getAreaSession, saveAreaSession } from '../common/utils';
import { HOLDAY_TYPE, EXCHANGE_STATUS } from '../operation/holiday-dialog/holiday-dialog.component';
import moment from 'moment';
import * as XLSX from 'xlsx';

// マスの中の担当（雇用指示の人）の1行
export interface ShortageStaff {
  sequenceNumber: number;
  staffDailyCleaning: number;
  nameStaff: string;
  // 時間帯の文字（勤務が無い日は null。祝日で時間が変わる日は「祝日 Xh出勤」）
  workingTime: string;
  hours: number;
  // 休み登録の種別（tbl_status.status の文字。無ければ null）
  status: string;
  shortHours: number;
  // 振替元：どの日に回したか（MM/DD。複数は「・」区切り）と、回した時間の合計
  exTo: string;
  exOutHours: number;
  // 振替先：どの日から回されたか・回されてきた時間
  exFrom: string;
  exInHours: number;
  startDate: string;
  endDate: string;
}

// マスの中の代勤の1行
export interface ShortageOp {
  id: number;
  name: string;
  staffDailyCleaning: number;
  status: string;
  time: string;
  hours: string;
  takeOver: boolean;
}

// 契約 × 日のマス
export interface ShortageDay {
  day: string;
  // 祝日／全社休日（どちらでもなければ null）
  holidayKind: string;
  hoursContract: number;
  contractTime: string;
  // その日に来る清掃員の時間（確定：雇用指示＋確定の代勤＋振替元。仮含む：＋代勤確定・未登録）
  staffKakutei: number;
  staffKari: number;
  // 引継ぎ・指導の代勤の時間（確定・仮には入れない。「引継ぎ含む」＝仮含む＋これ）
  takeOverHours: number;
  // 確定の時間差（マイナスが不足）と、仮（確定 ＋ 代勤確定・未登録）
  kakuteiDiff: number;
  kariDiff: number;
  staffs: ShortageStaff[];
  ops: ShortageOp[];
}

// 契約
export interface ShortageContract {
  cdContract: string;
  workName: string;
  // 表示期間に雇用指示が効いている担当（名前と清掃員コードだけ。押すと清掃員詳細ダイアログ）
  staffs: ShortageStaff[];
  days: ShortageDay[];
}

// 物件
export interface ShortageSite {
  cdSite: string;
  nameSite: string;
  address: string;
  areaName: string;
  kakuteiTotal: number;
  kariTotal: number;
  totalSites: number;
  contracts: ShortageContract[];
}

// 表の見出しの1日
export interface HeaderDay {
  day: string;
  md: string;
  dow: string;
  sat: boolean;
  sun: boolean;
  today: boolean;
  holidayKind: string;
}

@Component({
  selector: 'app-shortage-time',
  templateUrl: './shortage-time.component.html',
  styleUrls: ['./shortage-time.component.less']
})

export class ShortageTimeComponent implements OnInit, OnDestroy {

  // 表示期間の上限（日）。裏（ShortageTimeService.MAX_DAYS）と同じ
  readonly MAX_DAYS = 31;

  // 確定に入れる代勤の状況（稼働管理の「確定」と同じ）
  readonly CONFIRMED = ['●：代勤OK', '休み取り消し'];

  // 代勤の状況（並び・画面の文言・色は稼働管理と同じ。初期は確定の3つと仮に入れる2つを表示）
  statusList = [
    { value: '●：代勤OK', text: '●前日確認OK', cls: 's-ok', init: true },
    { value: '休み取り消し', text: '休み取り消し', cls: 's-holcancel', init: true },
    { value: 'スポット作業', text: 'スポット作業', cls: 's-spot', init: true },
    { value: '登録済み', text: '代勤確定', cls: 's-reg', init: true },
    { value: '未登録', text: '未登録', cls: 's-unknown', init: true },
    { value: '復帰確認', text: '復帰確認', cls: 's-req', init: false },
    { value: '×：代勤行けない', text: '×行けない', cls: 's-ng', init: false },
    { value: '×：連絡とれず', text: '×連絡とれず', cls: 's-nocontact', init: false },
    { value: '復帰不可・連絡取れず', text: '復帰不可', cls: 's-cant', init: false }
  ];
  // 表示する代勤の状況（押したものだけ出す）
  shown: { [status: string]: boolean } = {};

  // 期間（yyyy-MM-dd）。開いたときは今日から前回の長さ（初めては1日のみ）
  start = '';
  end = '';
  rangeDays = 1;

  // 合計の見方（kakutei：確定／kari：仮）。時間の絞り込みと並びに使う
  basis = 'kakutei';

  // 時間の絞り込み（期間の合計。何も押していなければ全部。押したものをもう一度押すと外れる）。開いたときは「不足」だけ ON
  times = { short: true, zero: false, over: false };
  timeChoices = [
    { key: 'short', name: '不足', cls: 't-short' },
    { key: 'zero', name: '過不足なし', cls: 't-zero' },
    { key: 'over', name: '過剰', cls: 't-over' }
  ];

  // 絞り込み
  nameSite = '';
  areas = [];
  areaIds: number[] = [];

  // 一覧（物件ごと）と見出しの日
  sites: ShortageSite[] = [];
  headerDays: HeaderDay[] = [];
  totalSites = 0;

  today = moment().format('YYYY-MM-DD');

  nowLoading = true;
  downloading = false;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  public headers = new HttpHeaders({
    "X-LoginAccessKey": localStorage.getItem("accessKey")
  });

  constructor(private http: HttpClient, private router: Router, private dialog: MatDialog, private cdr: ChangeDetectorRef, public mz: MobileZoomService) { }

  /**
   * ほかの画面へ移るときは viewport を元に戻す
   */
  ngOnDestroy() {
    this.mz.leave();
  }

  ngOnInit() {
    // スマホ：PC と同じ見た目を縮めて出す（MobileZoomService）
    this.mz.enter();

    // 検索条件で次に開いたときも残すのは物件名だけ（ユーザー決定）。ほかは初期値で開く
    this.statusList.forEach(s => this.shown[s.value] = s.init);
    this.nameSite = localStorage.getItem('shortage_name_site') || '';

    // エリアの初期値は稼働管理と同じ（ユーザ管理で決めた、その人のエリア）
    try {
      const userArea = JSON.parse(localStorage.getItem('filterArea') || 'null');
      if (userArea && Array.isArray(userArea)) {
        this.areaIds = userArea.map(a => Number(a));
      }
    } catch (e) { }

    // 期間は今日の1日だけ
    this.setRange(moment(), 1);

    this.getAreas();
  }

  ngAfterViewInit() {
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 20;
    this.getData();

    this.paginator.page.subscribe(() => {
      this.getData();
    });

    this.cdr.detectChanges();
  }

  /**
   * 取得に失敗したとき。サインインが切れているときだけ最初の画面へ戻す
   * @returns 最初の画面へ戻したら true
   */
  isSignOut(error): boolean {
    if (error && (error.status == 401 || error.status == 403)) {
      alert("認証に失敗しました");
      this.router.navigate(['/']);
      return true;
    }
    return false;
  }

  /**
   * エリアのドロップダウン
   * API: area?type=1
   */
  getAreas() {
    const area = getAreaSession();
    if (area && area.length) {
      this.areas = area;
      this.keepExistingAreas();
      return;
    }
    this.http.get(
      API_BASE_URL + "/api/v1/area?type=1", {headers: this.headers}
    ).subscribe(res => {
      this.areas = res as any[];
      saveAreaSession(this.areas);
      this.keepExistingAreas();
    }, error => {
      if (this.isSignOut(error)) return;
      alert('エリアの取得に失敗しました。');
    });
  }

  /**
   * 選んでいるエリアのうち、ドロップダウンに無いもの（消されたエリア）を外す（稼働管理と同じ）
   */
  keepExistingAreas() {
    if (!this.areas || !this.areas.length || !this.areaIds.length) return;
    const ids = new Set(this.areas.map(a => a.areaId));
    this.areaIds = this.areaIds.filter(id => ids.has(id));
  }

  /**
   * 期間を決める（開始日と日数）
   */
  setRange(start: moment.Moment, days: number) {
    this.rangeDays = days;
    this.start = start.format('YYYY-MM-DD');
    this.end = start.clone().add(days - 1, 'days').format('YYYY-MM-DD');
  }

  /**
   * 期間の長さ（1日のみ／3日間／1週間／2週間のボタン）。開始日はそのままで、終了日だけ変える
   * （前は「今日から n 日」だった。開始日を選んでから長さを選べるように、開始日は動かさない。ユーザー希望）
   */
  quickRange(days: number) {
    this.setRange(moment(this.start || undefined), days);
    this.search();
  }

  /**
   * 開始日を今日にする（長さはそのまま）
   */
  goToday() {
    this.setRange(moment(), this.rangeDays);
    this.search();
  }

  /**
   * 前の期間・次の期間（同じ長さで動かす）
   */
  shiftRange(direction: number) {
    this.setRange(moment(this.start).add(direction * this.rangeDays, 'days'), this.rangeDays);
    this.search();
  }

  /**
   * 開始日・終了日を手で変えたとき
   * 開始日を変えたら、長さ（1日のみ／3日間…）はそのままで終了日を合わせる。終了日を変えたら、長さをそれに合わせる（上限を超えたら縮める）
   */
  changePeriod(changedStart: boolean) {
    if (!this.start || !this.end) return;
    let s = moment(this.start);
    let e = moment(this.end);
    if (changedStart) {
      e = s.clone().add(this.rangeDays - 1, 'days');
    } else if (e.isBefore(s)) {
      s = e.clone();
    }
    if (e.diff(s, 'days') + 1 > this.MAX_DAYS) {
      alert('表示できる期間は ' + this.MAX_DAYS + ' 日までです。');
      if (changedStart) { e = s.clone().add(this.MAX_DAYS - 1, 'days'); } else { s = e.clone().add(-(this.MAX_DAYS - 1), 'days'); }
    }
    this.start = s.format('YYYY-MM-DD');
    this.end = e.format('YYYY-MM-DD');
    this.rangeDays = e.diff(s, 'days') + 1;
    this.search();
  }

  /**
   * 合計の見方（確定／仮）を切り替えたとき
   */
  changeBasis(basis: string) {
    if (this.basis == basis) return;
    this.basis = basis;
    this.search();
  }

  /**
   * 時間のチップを押したとき
   */
  toggleTime(key: string) {
    this.times[key] = !this.times[key];
    this.search();
  }

  /**
   * 代勤の状況のチップを押したとき（表示だけ変える。取り直さない）
   */
  toggleShown(status: string) {
    this.shown[status] = !this.shown[status];
  }

  /**
   * 検索条件を変えたとき（1ページ目から）
   */
  search() {
    this.paginator.pageIndex = 0;
    this.getData();
  }

  /**
   * 検索条件の文字（一覧とエクスポートで同じ）
   */
  queryString(): string {
    const times = Object.keys(this.times).filter(k => this.times[k]).join(',');
    return 'start=' + this.start
      + '&end=' + this.end
      + '&basis=' + this.basis
      + '&times=' + times
      + '&siteName=' + encodeURIComponent(this.nameSite)
      + '&areaIds=' + (this.areaIds || []).join(',');
  }

  /**
   * 一覧の取得（物件ごと。ページは物件数）
   * API: shortageTime
   */
  getData() {
    // 次に開いたときも残すのは物件名だけ
    localStorage.setItem('shortage_name_site', this.nameSite);

    this.nowLoading = true;

    this.http.get(
      API_BASE_URL + '/api/v1/shortageTime?index=' + this.paginator.pageIndex
      + '&size=' + this.paginator.pageSize
      + '&' + this.queryString(),
      {headers: this.headers}
    )
    .subscribe(res => {
      const data = (res as ShortageSite[]) || [];
      this.sites = data;
      this.totalSites = data.length > 0 ? data[0].totalSites : 0;
      this.headerDays = this.buildHeaderDays(data);
      this.nowLoading = false;
    },
    error => {
      this.nowLoading = false;
      if (this.isSignOut(error)) return;
      alert('不足時間確認の取得に失敗しました。');
    });
  }

  /**
   * 表の見出しの日（期間の日。祝日・全社休日はどれかのマスの値から）
   */
  buildHeaderDays(data: ShortageSite[]): HeaderDay[] {
    const kinds: { [day: string]: string } = {};
    data.forEach(site => site.contracts.forEach(con => con.days.forEach(d => {
      if (d.holidayKind) kinds[d.day] = d.holidayKind;
    })));
    const list: HeaderDay[] = [];
    const dows = ['日', '月', '火', '水', '木', '金', '土'];
    for (let m = moment(this.start); !m.isAfter(moment(this.end)); m.add(1, 'days')) {
      const day = m.format('YYYY-MM-DD');
      list.push({
        day: day,
        md: m.format('M/DD'),
        dow: dows[m.day()],
        sat: m.day() == 6,
        sun: m.day() == 0,
        today: day == this.today,
        holidayKind: kinds[day] || ''
      });
    }
    return list;
  }

  /**
   * 契約の行の、見出しの日に当たるマス（並びは SQL が日の順なので index で取れるが、念のため日で探す）
   */
  cellOf(con: ShortageContract, h: HeaderDay): ShortageDay {
    return con.days.find(d => d.day == h.day);
  }

  /**
   * 契約も人も代勤も無いマスか（「-」で出す）
   */
  isEmptyCell(cell: ShortageDay): boolean {
    if (!cell) return true;
    const hasOps = cell.ops && cell.ops.length > 0;
    const hasStaffs = cell.staffs && cell.staffs.length > 0;
    return Number(cell.hoursContract) == 0 && !hasStaffs && !hasOps && Number(cell.kakuteiDiff) == 0;
  }

  /**
   * 物件のその日の合計（契約の行のマスを足す）
   * any：その日に契約・人・代勤のあるマスが1つでもあるか（無ければ「-」）
   */
  siteDayTotal(site: ShortageSite, h: HeaderDay): { any: boolean, contract: number, staffKakutei: number, staffKari: number, takeOver: number, kakutei: number, kari: number } {
    const t = { any: false, contract: 0, staffKakutei: 0, staffKari: 0, takeOver: 0, kakutei: 0, kari: 0 };
    (site.contracts || []).forEach(con => {
      const cell = this.cellOf(con, h);
      if (this.isEmptyCell(cell)) return;
      t.any = true;
      t.contract += Number(cell.hoursContract) || 0;
      t.staffKakutei += Number(cell.staffKakutei) || 0;
      t.staffKari += Number(cell.staffKari) || 0;
      // 引継ぎ・指導の代勤の時間（時間差には入れない。「引継ぎ含む」の清掃員時間用）
      t.takeOver += Number(cell.takeOverHours) || 0;
      t.kakutei += Number(cell.kakuteiDiff) || 0;
      t.kari += Number(cell.kariDiff) || 0;
    });
    // 小数の誤差を落とす
    Object.keys(t).forEach(k => { if (k != 'any') t[k] = Math.round(t[k] * 100) / 100; });
    return t;
  }

  /**
   * マスの状態（色）。不足の色は「その日の物件の合計」で決める（ほかの契約の余りで埋まっている日は赤にしない。稼働管理と同じ考え方）
   * none：契約も人も代勤も無い日／short：この契約が不足で、物件の合計も確定・仮含むとも不足／kari：この契約が不足で、物件の合計が確定は不足だが仮含むで埋まる
   * cover：確定の代勤で埋まっている／ok：不足なし（物件の合計で埋まっている場合を含む）
   * @param cell マス
   * @param dayTotal その日の物件の合計（siteDayTotal）
   */
  cellState(cell: ShortageDay, dayTotal: { kakutei: number, kari: number }): string {
    if (this.isEmptyCell(cell)) return 'none';
    const hasOps = cell.ops && cell.ops.length > 0;
    if (Number(cell.kakuteiDiff) < 0 && dayTotal.kakutei < 0) {
      return dayTotal.kari >= 0 ? 'kari' : 'short';
    }
    const confirmed = hasOps && cell.ops.some(o => this.CONFIRMED.indexOf(o.status) >= 0 && !o.takeOver);
    return confirmed ? 'cover' : 'ok';
  }

  /**
   * 表示する代勤（押している状況だけ）
   */
  shownOps(cell: ShortageDay): ShortageOp[] {
    return (cell.ops || []).filter(o => this.shown[o.status]);
  }

  /**
   * 表示していない代勤の数
   */
  hiddenOpCount(cell: ShortageDay): number {
    return (cell.ops || []).length - this.shownOps(cell).length;
  }

  /**
   * 代勤の状況の文言・色（稼働管理と同じ）
   */
  statusText(status: string): string {
    const s = this.statusList.find(x => x.value == status);
    return s ? s.text : status;
  }

  statusClass(status: string): string {
    const s = this.statusList.find(x => x.value == status);
    return s ? s.cls : '';
  }

  /**
   * 休み登録の札の文言（種別名。振替元は「→ どの日へ 回した時間」、短時間は実働）
   */
  holidayText(st: ShortageStaff): string {
    if (st.status === null || st.status === undefined || st.status === '') return '';
    const type = HOLDAY_TYPE.find(h => String(h.key) == String(st.status));
    let text = type ? type.val : '休み';
    if (EXCHANGE_STATUS.indexOf(Number(st.status)) >= 0 && st.exTo) {
      text += '　→ ' + st.exTo + ' へ';
      // 回した時間（ユーザー希望 2026-10-05「振替（休み） → 09/22 へ ●h」）
      if (st.exOutHours !== null && st.exOutHours !== undefined) {
        text += ' ' + this.num(st.exOutHours) + 'h';
      }
    } else if (String(st.status) == '12' && st.shortHours !== null && st.shortHours !== undefined) {
      text += '　実働 ' + this.num(st.shortHours) + 'h';
    }
    return text;
  }

  /**
   * その日に働くか（休み登録が無いか、短時間（12）のとき）。休み・有休・振替元などで働かない人は、時間帯の行を出さず休みの札だけにする
   */
  worksThatDay(st: ShortageStaff): boolean {
    if (st.status === null || st.status === undefined || st.status === '') return true;
    return String(st.status) == '12';
  }

  /**
   * 振替元か（札の色を変える）
   */
  isExchange(st: ShortageStaff): boolean {
    return st.status !== null && st.status !== undefined && EXCHANGE_STATUS.indexOf(Number(st.status)) >= 0;
  }

  /**
   * 時間の数字（小数2桁まで。末尾の 0 は出さない）
   */
  num(val): string {
    if (val === null || val === undefined || val === '') return '';
    return String(parseFloat(Number(val).toFixed(2)));
  }

  /**
   * 過不足の表示（符号付き。マイナスは −）
   */
  diffText(val): string {
    if (val === null || val === undefined) return '';
    const n = Number(val);
    if (n === 0) return '±0 h';
    return (n > 0 ? '+' : '−') + this.num(Math.abs(n)) + ' h';
  }

  /**
   * 過不足の色（マイナス・プラス・0）
   */
  diffClass(val): string {
    const n = Number(val);
    return n < 0 ? 'minus' : n > 0 ? 'plus' : 'zero';
  }

  /**
   * 物件が不足か（選んでいる方の期間の合計がマイナス。帯を赤くする）
   */
  siteShort(site: ShortageSite): boolean {
    return Number(this.basis == 'kari' ? site.kariTotal : site.kakuteiTotal) < 0;
  }

  /**
   * 物件の帯の「仮含む」の見た目
   * 確定が不足で仮含むなら埋まる物件は、マスの黄色と同じ札で目立たせる（確定・仮含むのどちらで見ていても）
   */
  kariTotalClass(site: ShortageSite): string {
    if (Number(site.kakuteiTotal) < 0 && Number(site.kariTotal) >= 0) return 'kari-ok';
    return this.basis == 'kari' ? this.diffClass(site.kariTotal) : 'sub';
  }

  /**
   * 物件の帯の合計の文言（プラスのときだけ「（過剰）」）
   */
  totalText(val): string {
    const n = Number(val);
    let text = this.diffText(n);
    if (n > 0) text += '（過剰）';
    return text;
  }

  /**
   * 稼働管理を別タブで開く（この期間・この物件名で絞る）
   */
  openOperation(site: ShortageSite) {
    const url = location.origin + location.pathname + '#/operations/false/' + this.start + '/' + this.end + '?siteName=' + encodeURIComponent(site.nameSite || '');
    window.open(url, '_blank', 'noopener');
  }

  /**
   * MAP（物件名と、住所の市区町村までを入れて検索する）
   */
  openMap(site: ShortageSite) {
    this.dialog.open(MapDialogComponent, {
      maxWidth: '95vw',
      data: {
        siteName: site.nameSite,
        address: this.cityPart(site.address)
      }
    });
  }

  /**
   * 清掃員詳細ダイアログ（この物件が選ばれた状態で開く）
   */
  openStaffDetail(site: ShortageSite, st: ShortageStaff) {
    this.dialog.open(StaffDetailDialogComponent, {
      data: {
        data: { staffDailyCleaning: st.staffDailyCleaning, cdSite: site.cdSite }
      }
    });
  }

  /**
   * 契約詳細を別タブで開く（この物件コードで絞る。契約コードでは絞らない）
   */
  openContractDetail(site: ShortageSite) {
    const url = location.origin + location.pathname + '#/contractDetail/' + encodeURIComponent(site.cdSite);
    window.open(url, '_blank', 'noopener');
  }

  /**
   * 住所の市区町村まで（例：東京都江東区東雲1-1-1 → 東京都江東区）
   */
  cityPart(address: string): string {
    if (!address) return '';
    const m = address.match(/^(.{2,3}?[都道府県])?(.+?[市区町村])/);
    return m ? m[0] : address;
  }

  /**
   * エクスポート（今の検索条件で全部。契約 × 日の行）
   * API: shortageTime/allData
   */
  downloadBtn() {
    this.downloading = true;

    this.http.get(
      API_BASE_URL + '/api/v1/shortageTime/allData?' + this.queryString(),
      {headers: this.headers}
    )
    .subscribe(res => {
      const data = (res as ShortageSite[]) || [];
      const rows = [];
      const dows = ['日', '月', '火', '水', '木', '金', '土'];
      data.forEach(site => {
        site.contracts.forEach(con => {
          con.days.forEach(d => {
            const m = moment(d.day);
            rows.push({
              '物件名': site.nameSite,
              '物件コード': site.cdSite,
              'エリア': site.areaName || '',
              '住所': site.address,
              '契約コード': con.cdContract,
              '作業内容': con.workName || '',
              '日付': m.format('YYYY/MM/DD'),
              '曜日': dows[m.day()],
              '祝日': d.holidayKind || '',
              '契約時間帯': Number(d.hoursContract) > 0 ? d.contractTime : '',
              '契約時間': Number(d.hoursContract) > 0 ? Number(d.hoursContract) : '',
              '担当': (d.staffs || []).filter(st => st.workingTime).map(st => st.nameStaff + ' ' + st.workingTime + ' ' + this.num(st.hours) + 'h').join('、'),
              '休み登録・振替': (d.staffs || []).filter(st => st.status || st.exFrom).map(st => {
                const parts = [];
                if (st.status) parts.push(this.holidayText(st) + '（' + st.nameStaff + '）');
                if (st.exFrom) parts.push('振替指定日 +' + this.num(st.exInHours) + 'h（' + st.exFrom + ' から。' + st.nameStaff + '）');
                return parts.join('、');
              }).join('、'),
              '代勤': (d.ops || []).map(o => this.statusText(o.status) + ' ' + o.name + ' ' + o.time + ' ' + (o.hours || '') + 'h' + (o.takeOver ? '（引継ぎ・指導）' : '')).join('、'),
              '引継ぎ・指導の時間': Number(d.takeOverHours) > 0 ? Number(d.takeOverHours) : '',
              '確定': Number(d.kakuteiDiff),
              '仮含む': Number(d.kariDiff),
              '物件の期間合計（確定）': Number(site.kakuteiTotal),
              '物件の期間合計（仮含む）': Number(site.kariTotal)
            });
          });
        });
      });

      if (rows.length > 0) {
        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, '不足時間確認' + moment(this.start).format('YYYYMMDD') + '-' + moment(this.end).format('YYYYMMDD') + '.xlsx');
      } else {
        alert('出力するデータがありません。');
      }
      this.downloading = false;
    },
    error => {
      this.downloading = false;
      if (this.isSignOut(error)) return;
      alert('エクスポートに失敗しました。');
    });
  }
}
